export type WorkflowValidationManualTypeId = 'approved' | 'rejected';

export type WorkflowValidationProcessApiType = 'ok' | 'ko' | 'ok-ko';

/**
 * Extrae ids `approved` / `rejected` desde el valor del tec-toggle (simple o múltiple).
 * Soporta legado `approved_rejected` (un solo botón) mapeándolo a ambos.
 */
export function normalizeValidationManualTypeIds(raw: unknown): WorkflowValidationManualTypeId[] {
	const ids = extractRawIds(raw);
	const selected = new Set<WorkflowValidationManualTypeId>();

	for (const id of ids) {
		if (id === 'approved' || id === 'rejected') {
			selected.add(id);
			continue;
		}
		if (id === 'approved_rejected') {
			selected.add('approved');
			selected.add('rejected');
		}
	}

	if (selected.size === 0) {
		return ['approved'];
	}

	return (['approved', 'rejected'] as const).filter((id) => selected.has(id));
}

/** Mapea selección UI → contrato API (`ok` | `ko` | `ok-ko`). */
export function mapValidationManualTypesToApi(
	raw: unknown,
): WorkflowValidationProcessApiType {
	const ids = normalizeValidationManualTypeIds(raw);
	const hasApproved = ids.includes('approved');
	const hasRejected = ids.includes('rejected');

	if (hasApproved && hasRejected) {
		return 'ok-ko';
	}
	if (hasRejected) {
		return 'ko';
	}
	return 'ok';
}

/** Mapea tipo API → selección UI (multi). Acepta legado `ok_ko`. */
export function mapValidationApiTypeToManualTypes(
	rawType: unknown,
): WorkflowValidationManualTypeId[] {
	if (rawType === 'ok') {
		return ['approved'];
	}
	if (rawType === 'ko') {
		return ['rejected'];
	}
	if (rawType === 'ok-ko' || rawType === 'ok_ko') {
		return ['approved', 'rejected'];
	}
	return ['approved'];
}

export function isValidationManualRejectedSelected(raw: unknown): boolean {
	return normalizeValidationManualTypeIds(raw).includes('rejected');
}

function extractRawIds(raw: unknown): string[] {
	if (Array.isArray(raw)) {
		return raw.map((item) => {
			if (typeof item === 'object' && item && 'id' in item) {
				return String((item as { id: unknown }).id);
			}
			return String(item);
		});
	}

	if (typeof raw === 'object' && raw && 'id' in raw) {
		return [String((raw as { id: unknown }).id)];
	}

	if (raw === null || raw === undefined || raw === '') {
		return [];
	}

	return [String(raw)];
}
